import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createTransaction, createAuditLog } from '../graphql/mutations';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import './TransactionForm.css';

const client = generateClient({
  authMode: 'userPool'
});

interface TransactionFormProps {
  userId: string;
  onTransactionCreated: () => void;
}

const TransactionForm: React.FC<TransactionFormProps> = ({ userId, onTransactionCreated }) => {
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0], // Just the date part for the input
    type: 'EXPENSE' as 'EXPENSE' | 'INCOME',
    category: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const input = {
        amount: parseFloat(formData.amount),
        description: formData.description,
        date: new Date(formData.date + 'T12:00:00').toISOString(), // Use selected date with noon as default time
        type: formData.type,
        category: formData.category,
        createdByID: userId || 'unknown-user'
      };

      const result = await client.graphql({
        query: createTransaction,
        variables: { input }
      }) as GraphQLResult<{ createTransaction: { id: string } }>;

      if (!result.data?.createTransaction) {
        throw new Error('Failed to create transaction');
      }

      // Create audit log
      await client.graphql({
        query: createAuditLog,
        variables: {
          input: {
            action: 'CREATE',
            entityType: 'Transaction',
            entityId: result.data.createTransaction.id,
            changes: `Creó ${input.type === 'EXPENSE' ? 'un gasto' : 'un ingreso'} por $${input.amount.toFixed(2)}: "${input.description}" (Categoría: ${input.category || 'No especificada'})`,
            userID: userId
          }
        }
      });

      setFormData({
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        type: 'EXPENSE',
        category: ''
      });
      onTransactionCreated();
    } catch (err) {
      console.error('Full error details:', JSON.stringify(err, null, 2));
      if (err && typeof err === 'object' && 'errors' in err) {
        const graphqlError = err as { errors: Array<{ message: string; path: string[] | null; locations: Array<{ line: number; column: number }> }> };
        console.error('GraphQL errors:', JSON.stringify(graphqlError.errors, null, 2));
        if (graphqlError.errors && graphqlError.errors.length > 0) {
          const error = graphqlError.errors[0];
          console.error('Error details:', {
            message: error.message,
            path: error.path,
            locations: error.locations
          });
          setError(error.message);
        } else {
          setError('Error creating transaction');
        }
      } else if (err instanceof Error) {
        console.error('Error message:', err.message);
        setError(err.message);
      } else {
        setError('Error creating transaction');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="transaction-form-container">
      <h2>Nueva Transacción</h2>
      <form onSubmit={handleSubmit} className="transaction-form">
        <div className="form-group">
          <label htmlFor="date">Fecha:</label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            required
            lang="es"
          />
        </div>

        <div className="form-group">
          <label htmlFor="type">Tipo de Transacción:</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            required
          >
            <option value="EXPENSE">Gasto</option>
            <option value="INCOME">Ingreso</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="description">Descripción:</label>
          <input
            type="text"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="amount">Monto ($):</label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={formData.amount}
            onChange={handleInputChange}
            required
            step="0.01"
            min="0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Categoría:</label>
          <input
            type="text"
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            placeholder="Category (optional)"
            className="w-full p-2 border rounded"
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" disabled={loading} className="submit-button">
          {loading ? 'Creando...' : 'Crear Transacción'}
        </button>
      </form>
    </div>
  );
};

export default TransactionForm; 